import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@restworld/utils-common';

/**
 * Heading size scale (1 = largest, 6 = smallest).
 * Each size maps to a default HTML heading tag (h1-h6)
 * but can be overridden with the `as` prop for semantic flexibility.
 *
 * Size scale:
 * - 1: 36px/54px → 30px/45px mobile (h1)
 * - 2: 30px/45px → 24px/36px mobile (h2)
 * - 3: 24px/36px (h3)
 * - 4: 20px/30px (h4)
 * - 5: 16px/24px (h5)
 * - 6: 14px/21px (h6)
 */
const headingVariants = cva('', {
  variants: {
    size: {
      1: 'lg:text-4xl lg:leading-[54px] text-[30px] leading-[45px]',
      2: 'lg:text-[30px] lg:leading-[45px] text-2xl leading-9',
      3: 'lg:text-2xl lg:leading-9 text-2xl leading-9',
      4: 'lg:text-xl lg:leading-[30px] text-xl leading-[30px]',
      5: 'lg:text-base lg:leading-6 text-base leading-6',
      6: 'lg:text-sm lg:leading-[21px] text-sm leading-[21px]',
    },
    weight: {
      light: 'font-light',
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
      extrabold: 'font-extrabold',
    },
    color: {
      default: 'text-gray-900',
      muted: 'text-gray-500',
      'muted-foreground': 'text-gray-600',
      primary: 'text-primary-800',
      'primary-light': 'text-primary-700',
      secondary: 'text-secondary-800',
      error: 'text-red-600',
      'error-dark': 'text-red-800',
      success: 'text-green-700',
      warning: 'text-orange-700',
      white: 'text-white',
      'white-muted': 'text-gray-50',
      purple: 'text-purple-700',
      'purple-light': 'text-purple-500',
      neutral: 'text-neutral-700',
      'neutral-light': 'text-neutral-200',
      'neutral-lightest': 'text-neutral-100',
      'neutral-dark': 'text-neutral-10',
    },
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },
    truncate: {
      true: 'truncate',
    },
  },
  defaultVariants: {
    size: 4,
    weight: 'semibold',
    color: 'default',
  },
});

/** Maps size to default HTML heading tag */
const sizeToTag = {
  1: 'h1',
  2: 'h2',
  3: 'h3',
  4: 'h4',
  5: 'h5',
  6: 'h6',
} as const;

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span' | 'div' | 'p';

export type HeadingProps = VariantProps<typeof headingVariants> & {
  /** Override the rendered HTML element. Defaults to the tag matching `size` (h1-h6). */
  as?: HeadingTag;
  children?: React.ReactNode;
  className?: string;
  /** Max characters before truncation with ellipsis */
  maxChars?: number;
  /** Test ID for e2e tests */
  testId?: string;
};

export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ as, size = 4, weight, color, align, truncate, children, className, maxChars, testId, ...props }, ref) => {
    const Tag = as || sizeToTag[size as keyof typeof sizeToTag] || 'h4';

    let content = children;
    if (maxChars && typeof children === 'string' && children.length > maxChars) {
      content = children.slice(0, maxChars) + '…';
    }

    return React.createElement(
      Tag,
      {
        ref,
        className: cn(headingVariants({ size, weight, color, align, truncate }), className),
        'data-cy': testId,
        ...props,
      },
      content
    );
  }
);
Heading.displayName = 'Heading';

export { headingVariants };
export default Heading;
